import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { Alert, Button, Container, Form } from "react-bootstrap";
import { useMsrManagementService } from "../services/msrManagementService";
import { useGeneralApiCall } from "../services/useGeneralApiCall";
import { showToast } from "@/api/notification";
import { GiCrossMark } from "react-icons/gi";
import { useNavigate } from "react-router-dom";

type OptionType = { id?: string; value: string; label: string };

type CreateNewMSRCategoryProps = {
  year_label?: string;
  season_label?: string;
  cover_image_label?: string;
  year_value?: number;
  year_slug?: string;
  season_value?: string;
  season_slug?: string;
  isEditing?: boolean;
  setIsEditing?: (value: boolean) => void;
  edit_button_visible?: boolean;
  create_button_visible?: boolean;
  handleUpdateMsrCategory?: (formData: FormData) => void;
  getMsrYearWiseSeason: () => void;
  onHide?: () => void;
  singleApiData?: () => void;
};

const CreateNewMSRCategory: React.FC<CreateNewMSRCategoryProps> = ({
  year_label = "Year",
  season_label = "Season",
  cover_image_label = "Cover Image",
  year_value = 0,
  year_slug = "",
  season_value = "",
  season_slug = "",
  isEditing = false,
  setIsEditing = () => {},
  edit_button_visible = false,
  create_button_visible = true,
  handleUpdateMsrCategory = () => {},
  getMsrYearWiseSeason = () => {},
  singleApiData = () => {},
}) => {
  const [years, setYears] = useState<OptionType[]>([]);
  const [categories, setCategories] = useState<OptionType[]>([]);
  const [tempYear, setTempYear] = useState<string>(
    year_value.toString() ? year_value.toString() : ""
  );
  const [mstTempType, setMstTempType] = useState<string>(season_value);
  const { postApi, deleteApi } = useGeneralApiCall();
  const [error, setError] = useState<string>("");

  const initialYearOption: OptionType | null =
    year_value != null
      ? { value: year_value.toString(), label: year_value.toString() }
      : null;
  const initialCategoryOption: OptionType | null = season_value
    ? { value: season_value, label: season_value }
    : null;

  const [selectedYear, setSelectedYear] = useState<OptionType | null>(
    initialYearOption
  );
  const [selectedCategory, setSelectedCategory] = useState<OptionType | null>(
    initialCategoryOption
  );

  const navigate = useNavigate();

  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [previewURL, setPreviewURL] = useState<string>();

  const handleCreateYear = (inputValue: string) => {
    if (!/^\d{4}$/.test(inputValue)) {
      alert("Please enter a valid year (YYYY).");
      return;
    }
    const numericYear = parseInt(inputValue, 10);
    if (numericYear < 2000 || numericYear > 2100) {
      alert("Please enter a year between 2000 and 2100.");
      return;
    }
    const newOption: OptionType = { value: inputValue, label: inputValue };
    setYears((prev) => [...prev, newOption]);
    setSelectedYear(newOption);
    setTempYear(newOption.value);
  };

  const handleCreateCategory = (inputValue: string) => {
    const newOption: OptionType = { value: inputValue, label: inputValue };
    setCategories((prev) => [...prev, newOption]);
    setSelectedCategory(newOption);
    setMstTempType(newOption.value);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setCoverImage(file);
      setPreviewURL(URL.createObjectURL(file));
    } else {
      alert("Please select a valid image file.");
    }
  };

  const handleDeleteSeason = () => {
    try {
      deleteApi(
        `api/msr/v1/delete-msr/${season_slug}/?year_slug=${year_slug}`
      ).then((res) => {
        showToast({
          message: res.message || "Season deleted successfully",
          type: "success",
        });
        navigate("/msr-category-and-years-management");
      });
    } catch (error) {
      // console.error("Error deleting season:", error);
      showToast({ message: "Failed to delete season.", type: "error" });
    }
  };

  // Unified create/update handler
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!selectedYear || !selectedCategory) {
      alert("Please select both year and category.");
      return;
    }

    const formData = new FormData();
    if (tempYear) {
      formData.append("year_value", tempYear);
    } else {
      formData.append("msr_year_id", selectedYear.id ?? "");
    }

    if (mstTempType) {
      formData.append("season_name", mstTempType);
    } else {
      formData.append("msr_season_id", selectedCategory.id ?? "");
    }

    if (coverImage) {
      formData.append("cover_image", coverImage);
    }

    if (create_button_visible) {
      try {
        const response: any = await postApi(
          "api/msr/v1/msr/msr_season_year_create_together/",
          formData
        );
        if (response.status === 201) {
          getMsrYearWiseSeason();
          singleApiData();
          showToast({ message: response.message, type: "success" });
        }
      } catch (error: any) {
        setError(error.message);
      }
    } else if (edit_button_visible && isEditing) {
      handleUpdateMsrCategory(formData);
    }
  };

  const { fetchMsrYear, fetchMsrSeason } = useMsrManagementService();

  useEffect(() => {
    const getMsrYear = async () => {
      const response: any = await fetchMsrYear();
      const data = response.data.results;

      const mappedYears: OptionType[] = data.map((item: any) => ({
        id: item.msr_year_id,
        value: item.year,
        label: item.year,
      }));
      setYears(mappedYears);
    };
    getMsrYear();
  }, []);

  useEffect(() => {
    const getMsrSeason = async () => {
      const response: any = await fetchMsrSeason();
      const data = response.data.results;

      const mappedSeasons: OptionType[] = data.map((item: any) => ({
        id: item.msr_season_id,
        value: item.season_name,
        label: item.season_name,
      }));
      setCategories(mappedSeasons);
    };
    getMsrSeason();
  }, []);

  const isDisabled = !isEditing && !create_button_visible;

  return (
    <div className="mt-5 ml-5 mr-5 mb-5">
      <Container className="d-flex justify-content-between align-items-center mb-4">
        <h4>
          {edit_button_visible &&
          !create_button_visible &&
          selectedCategory &&
          selectedYear
            ? `${selectedCategory.label} - ${selectedYear.label}`
            : "Create MSR Category"}
        </h4>
        {edit_button_visible && !isEditing && (
          <Button onClick={() => setIsEditing(true)}>Edit</Button>
        )}
      </Container>

      <div style={{ border: "1px solid black", marginBottom: "10px" }}></div>

      <div className="mb-4">
        <label className="form-label">{year_label}</label>
        <CreatableSelect
          isClearable
          options={years}
          value={selectedYear}
          onChange={(option) => {
            setSelectedYear(option);
            setTempYear(option?.value || "");
          }}
          onCreateOption={handleCreateYear}
          placeholder="Choose or type a year..."
          isDisabled={isDisabled}
        />
      </div>

      <div className="mb-4">
        <label className="form-label">{season_label}</label>
        <CreatableSelect
          isClearable
          options={categories}
          value={selectedCategory}
          onChange={(option) => {
            setSelectedCategory(option);
            setMstTempType(option?.value || "");
          }}
          onCreateOption={handleCreateCategory}
          placeholder="Choose or type a category..."
          isDisabled={isDisabled}
        />
      </div>

      <div className="mb-4">
        <label className="form-label">{cover_image_label}</label>
        <Form.Control
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          disabled={isDisabled}
        />
        {previewURL && isEditing && (
          <div className="mt-3">
            <p className="mb-1">Preview:</p>
            <img
              src={previewURL}
              alt="Cover Preview"
              style={{ height: "400px", objectFit: "cover", width: "auto" }}
            />
          </div>
        )}
      </div>

      {edit_button_visible && isEditing && (
        <div className="mb-4 d-flex gap-2">
          <Button type="submit" variant="primary" onClick={handleSubmit}>
            Save Changes
          </Button>
          <Button variant="danger" onClick={handleDeleteSeason}>
            Delete Season
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setIsEditing(false);
              setCoverImage(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {create_button_visible && (
        <Button type="submit" onClick={handleSubmit}>
          Create MSR Category
        </Button>
      )}

      {error && (
        <Alert variant="danger" className="mt-2">
          <div className="d-flex justify-content-between  align-items-center">
            <p>{error}</p>

            <GiCrossMark
              onClick={() => setError("")}
              className="cursor-pointer "
            />
          </div>
        </Alert>
      )}
    </div>
  );
};

export default CreateNewMSRCategory;
